import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Typography,
  Divider,
  Alert,
  Box,
  Stack,
  Chip,
} from '@mui/material'
import { Language as LanguageIcon } from '@mui/icons-material'
import { User, UserDetail, Language } from '../../types/user'
import { UserService } from '../../services/userService'
import { useAuth } from '../../contexts/AuthContext'
import { SUPPORTED_LANGUAGES } from '../../i18n'

interface UserEditModalProps {
  open: boolean
  user: User | null
  onClose: () => void
  onSuccess: (message: string) => void
}

interface UserEditForm {
  name: string
  email: string
  department: string
  position: string
  role: string
  isActive: boolean
  preferredLanguage: Language | ''
}

export const UserEditModal: React.FC<UserEditModalProps> = ({ open, user, onClose, onSuccess }) => {
  const { t } = useTranslation('user')
  const { user: currentUser } = useAuth()
  const [form, setForm] = useState<UserEditForm>({
    name: '',
    email: '',
    department: '',
    position: '',
    role: 'USER',
    isActive: true,
    preferredLanguage: '',
  })
  const [userDetail, setUserDetail] = useState<UserDetail | null>(null)
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [errors, setErrors] = useState<Record<string, string>>({})

  // Load user detail data
  useEffect(() => {
    if (open && user) {
      loadUserDetail(user.id)
      setForm({
        name: user.name,
        email: user.email,
        department: user.department || '',
        position: user.position || '',
        role: user.role,
        isActive: user.isActive,
        preferredLanguage: user.preferredLanguage || '',
      })
    }
  }, [open, user])

  const loadUserDetail = async (userId: string) => {
    try {
      setLoading(true)
      const detail = await UserService.getUser(userId)
      setUserDetail(detail)
    } catch (error) {
      console.error('Failed to load user detail:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleClose = () => {
    if (!saving) {
      setForm({
        name: '',
        email: '',
        department: '',
        position: '',
        role: 'USER',
        isActive: true,
        preferredLanguage: '',
      })
      setUserDetail(null)
      setErrors({})
      onClose()
    }
  }

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {}

    if (!form.name.trim()) {
      newErrors.name = t('form.validation.nameRequired')
    }

    if (!form.email.trim()) {
      newErrors.email = t('form.validation.emailRequired')
    } else if (!/\S+@\S+\.\S+/.test(form.email)) {
      newErrors.email = t('form.validation.emailInvalid')
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSave = async () => {
    if (!validateForm() || !user) return

    try {
      setSaving(true)

      // Use bulk update API (single user)
      const updates: {
        department?: string
        position?: string
        role?: string
        preferredLanguage?: Language
      } = {
        department: form.department || undefined,
        position: form.position || undefined,
        role: form.role !== user.role ? form.role : undefined,
        preferredLanguage:
          form.preferredLanguage && form.preferredLanguage !== user.preferredLanguage
            ? form.preferredLanguage
            : undefined,
      }

      if (Object.values(updates).some((v) => v !== undefined)) {
        await UserService.bulkUpdateUsers({
          userIds: [user.id],
          updates,
        })
      }

      // Call status change API if isActive changed
      if (form.isActive !== user.isActive) {
        await UserService.bulkUpdateStatus([user.id], form.isActive)
      }

      onSuccess(t('messages.updateSuccess'))
      handleClose()
    } catch (error) {
      console.error('User update error:', error)
      setErrors({ general: t('messages.loadError') })
    } finally {
      setSaving(false)
    }
  }

  const handleFormChange = (field: keyof UserEditForm, value: any) => {
    setForm((prev) => ({ ...prev, [field]: value }))
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }))
    }
  }

  const getRecentActivity = () => {
    if (!userDetail) return null

    const recentEnrollments = userDetail.enrollments.slice(0, 3)
    const recentResults = userDetail.testResults.slice(0, 3)

    return { recentEnrollments, recentResults }
  }

  const activity = getRecentActivity()

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth disableEscapeKeyDown={saving}>
      <DialogTitle>
        {t('edit.title')}: {user?.name}
      </DialogTitle>

      <DialogContent>
        <Stack spacing={3} sx={{ mt: 1 }}>
          {errors.general && <Alert severity="error">{errors.general}</Alert>}

          {/* 基本情報 */}
          <Box>
            <Typography variant="h6" gutterBottom>
              {t('common:sections.basicInfo')}
            </Typography>
            <Stack spacing={2}>
              <Stack direction="row" spacing={2}>
                <TextField
                  fullWidth
                  label={t('form.name')}
                  value={form.name}
                  onChange={(e) => handleFormChange('name', e.target.value)}
                  error={!!errors.name}
                  helperText={errors.name}
                  required
                />
                <TextField
                  fullWidth
                  label={t('form.email')}
                  value={form.email}
                  onChange={(e) => handleFormChange('email', e.target.value)}
                  error={!!errors.email}
                  helperText={errors.email}
                  required
                  disabled // メールアドレスは変更不可
                />
              </Stack>
              <Stack direction="row" spacing={2}>
                <TextField
                  fullWidth
                  label={t('form.department')}
                  value={form.department}
                  onChange={(e) => handleFormChange('department', e.target.value)}
                />
                <TextField
                  fullWidth
                  label={t('form.position')}
                  value={form.position}
                  onChange={(e) => handleFormChange('position', e.target.value)}
                />
              </Stack>
              <Stack direction="row" spacing={2}>
                <FormControl fullWidth>
                  <InputLabel>{t('form.role')}</InputLabel>
                  <Select
                    value={form.role}
                    label={t('form.role')}
                    onChange={(e) => handleFormChange('role', e.target.value)}
                  >
                    <MenuItem value="USER">{t('common:roles.user')}</MenuItem>
                    <MenuItem value="CORPORATE_ADMIN">{t('common:roles.admin')}</MenuItem>
                    {currentUser?.role === 'SUPER_ADMIN' && (
                      <MenuItem value="SUPER_ADMIN">{t('common:roles.superAdmin')}</MenuItem>
                    )}
                  </Select>
                </FormControl>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={form.isActive}
                        onChange={(e) => handleFormChange('isActive', e.target.checked)}
                      />
                    }
                    label={t('form.isActive')}
                  />
                </Box>
              </Stack>
              <FormControl fullWidth>
                <InputLabel>{t('profile.language')}</InputLabel>
                <Select
                  value={form.preferredLanguage}
                  label={t('profile.language')}
                  onChange={(e) => handleFormChange('preferredLanguage', e.target.value)}
                  startAdornment={<LanguageIcon sx={{ mr: 1, color: 'action.active' }} />}
                >
                  <MenuItem value="">{t('common:notSet', { defaultValue: '未設定' })}</MenuItem>
                  {SUPPORTED_LANGUAGES.map((lang) => (
                    <MenuItem key={lang.code} value={lang.code === 'ja' ? 'JA' : 'EN'}>
                      {lang.nativeLabel} ({lang.label})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>
          </Box>

          <Divider />

          {/* アクティビティ情報 */}
          {activity && (
            <Box>
              <Typography variant="h6" gutterBottom>
                {t('detail.tabs.profile')}
              </Typography>

              {/* 最近の受講 */}
              {activity.recentEnrollments.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    {t('detail.tabs.enrollments')} ({userDetail?.enrollments.length})
                  </Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap">
                    {activity.recentEnrollments.map((enrollment) => (
                      <Chip
                        key={enrollment.id}
                        label={enrollment.test.title}
                        size="small"
                        color={enrollment.completedAt ? 'success' : 'default'}
                        variant="outlined"
                      />
                    ))}
                    {userDetail!.enrollments.length > 3 && (
                      <Chip
                        label={`+${userDetail!.enrollments.length - 3}...`}
                        size="small"
                        variant="outlined"
                      />
                    )}
                  </Stack>
                </Box>
              )}

              {/* 最近のテスト結果 */}
              {activity.recentResults.length > 0 && (
                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    {t('detail.tabs.results')} ({userDetail?.testResults.length})
                  </Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap">
                    {activity.recentResults.map((result) => (
                      <Chip
                        key={result.id}
                        label={`${result.test.title}: ${result.score}`}
                        size="small"
                        color={
                          result.score >= 80 ? 'success' : result.score >= 60 ? 'warning' : 'error'
                        }
                        variant="outlined"
                      />
                    ))}
                    {userDetail!.testResults.length > 3 && (
                      <Chip
                        label={`+${userDetail!.testResults.length - 3}...`}
                        size="small"
                        variant="outlined"
                      />
                    )}
                  </Stack>
                </Box>
              )}

              {activity.recentEnrollments.length === 0 && activity.recentResults.length === 0 && (
                <Typography variant="body2" color="textSecondary">
                  {t('detail.noActivity')}
                </Typography>
              )}
            </Box>
          )}
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={saving}>
          {t('dialogs.cancel')}
        </Button>
        <Button onClick={handleSave} variant="contained" disabled={saving || loading}>
          {saving ? t('common:buttons.saving') : t('common:buttons.save')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
